import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import styles from './Field.module.css';
import { t } from '@/lib/i18n';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  /** Inline error. Its presence marks the input invalid for assistive tech. */
  error?: string | null;
  /** Left-hand hint under the input, e.g. "1 a 80 caracteres". */
  hint?: ReactNode;
  /** Shows "15/80" on the right when `maxLength` is set. */
  showCounter?: boolean;
}

export function Field({ label, error, hint, showCounter, id, ...input }: FieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;

  const value = typeof input.value === 'string' ? input.value : '';
  const describedBy = cx(error ? errorId : '', hint ? hintId : '').trim() || undefined;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={fieldId}>
        {label}
      </label>
      <div className={cx(styles.inputWrap, error && styles.invalid)}>
        <input
          {...input}
          id={fieldId}
          className={styles.input}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
        />
      </div>
      {error ? (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      ) : (
        (hint != null || showCounter) && (
          <div className={styles.footer}>
            <span id={hintId}>{hint}</span>
            {showCounter && input.maxLength != null && (
              <span className={styles.counter}>
                {value.length}/{input.maxLength}
              </span>
            )}
          </div>
        )
      )}
    </div>
  );
}

interface PasswordFieldProps extends Omit<FieldProps, 'type'> {
  /**
   * Renders the 3-segment strength meter. The backend requires 8 characters; the watch
   * app accepts 6, which is the single most common cause of a failed sign-up. Validating
   * here, before the request, is the whole point (canvas 1j).
   */
  showStrength?: boolean;
}

export function PasswordField({ showStrength = false, ...props }: PasswordFieldProps) {
  const [visible, setVisible] = useState(false);
  const autoId = useId();
  const fieldId = props.id ?? autoId;
  const errorId = `${fieldId}-error`;

  const value = typeof props.value === 'string' ? props.value : '';
  const { label, error, hint, ...input } = props;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={fieldId}>
        {label}
      </label>
      <div className={cx(styles.inputWrap, error && styles.invalid)}>
        <input
          {...input}
          id={fieldId}
          type={visible ? 'text' : 'password'}
          className={styles.input}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        <button
          type="button"
          className={styles.reveal}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? t('common.hidePassword') : t('common.showPassword')}
        >
          {visible ? t('common.hide') : t('common.show')}
        </button>
      </div>

      {showStrength && value.length > 0 ? (
        <PasswordStrength value={value} />
      ) : error ? (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      ) : hint != null ? (
        <div className={styles.footer}>
          <span>{hint}</span>
        </div>
      ) : null}
    </div>
  );
}

export const PASSWORD_MIN = 8;

function PasswordStrength({ value }: { value: string }) {
  const missing = PASSWORD_MIN - value.length;

  const level = missing > 0 ? 0 : value.length >= 12 ? 2 : 1;
  const fill = [styles.strengthWeak, styles.strengthOk, styles.strengthGood][level];
  const labelTone = [styles.strengthLabelWeak, styles.strengthLabelOk, styles.strengthLabelGood][
    level
  ];

  const message =
    missing > 0
      ? t('common.passwordMissing', { count: missing, min: PASSWORD_MIN })
      : level === 2
        ? t('common.passwordGood')
        : t('common.passwordOk');

  return (
    <div className={styles.strength}>
      <div className={styles.strengthBars} aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span key={i} className={cx(styles.strengthBar, i <= level && fill)} />
        ))}
      </div>
      <span className={cx(styles.strengthLabel, labelTone)} role="status">
        {message}
      </span>
    </div>
  );
}
